import { useRef } from 'react';

export function useRichTextEditor() {
  const editorRef = useRef<HTMLDivElement>(null);
  const savedSelectionRef = useRef<Range | null>(null);

  const applyFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const insertList = (ordered: boolean) => {
    applyFormat(ordered ? 'insertOrderedList' : 'insertUnorderedList');
  };

  const insertBlockquote = () => {
    const sel = window.getSelection();
    if (!sel || !sel.anchorNode) return;

    const existingBlockquote =
      sel.anchorNode.parentElement?.closest('blockquote');

    if (existingBlockquote) {
      // Toggle off: move the content out of the blockquote.
      const parent = existingBlockquote.parentNode;
      while (existingBlockquote.firstChild) {
        parent?.insertBefore(existingBlockquote.firstChild, existingBlockquote);
      }
      existingBlockquote.remove();
    } else {
      applyFormat('formatBlock', 'blockquote');
    }

    editorRef.current?.focus();
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const applyLink = (linkUrl: string) => {
    if (!linkUrl.trim()) return;

    const sel = window.getSelection();
    if (!sel || !savedSelectionRef.current) return;

    sel.removeAllRanges();
    sel.addRange(savedSelectionRef.current);

    const range = savedSelectionRef.current;
    const selectedText = range.toString().trim();

    if (selectedText) {
      document.execCommand('createLink', false, linkUrl);
    } else {
      // No selection: insert the URL as both text and link.
      const link = document.createElement('a');
      link.href = linkUrl;
      link.textContent = linkUrl;
      link.contentEditable = 'false';

      range.deleteContents();
      range.insertNode(link);

      // A non-breaking space after the link, so typing continues outside it.
      const space = document.createTextNode('\u00A0');
      link.parentNode?.insertBefore(space, link.nextSibling);

      range.setStartAfter(space);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }

    savedSelectionRef.current = null;
    editorRef.current?.focus();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    onSend: () => void,
  ) => {
    const sel = window.getSelection();
    if (!sel || !sel.anchorNode) return;

    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      onSend();
      return;
    }

    // Handle `Enter` in blockquote:
    // First `Enter` = new line inside of blockquote;
    // Second `Enter` (on an empty line) = exit.
    if (e.key === 'Enter') {
      const anchorEl =
        sel.anchorNode.nodeType === Node.ELEMENT_NODE
          ? (sel.anchorNode as Element)
          : sel.anchorNode.parentElement;
      const blockquote = anchorEl?.closest('blockquote');
      if (!blockquote) return;

      // Find the direct child of blockquote that contains the cursor.
      let line: Node | null = sel.anchorNode;
      while (line && line.parentNode !== blockquote) {
        line = line.parentNode;
      }

      const textIsEmpty = (node: Node | null) =>
        !node || !(node.textContent ?? '').trim();

      const lastChild = blockquote.lastChild;
      const cursorOnEmptyLastLine =
        !!line && line === lastChild && textIsEmpty(line);
      const trailingBr =
        (!line || line === blockquote) && lastChild?.nodeName === 'BR';
      const blockquoteIsEmpty =
        textIsEmpty(blockquote) && !blockquote.children.length;

      if (!cursorOnEmptyLastLine && !trailingBr && !blockquoteIsEmpty) {
        // First Enter, or Enter on a non-empty line: insert a <br> so the
        // cursor stays in the blockquote instead of the browser splitting it in
        // two.
        e.preventDefault();
        document.execCommand('insertLineBreak');
        return;
      }

      e.preventDefault();

      // Strip the trailing empty line/br so it doesn't linger in the blockquote.
      if (cursorOnEmptyLastLine && line && line.parentNode === blockquote) {
        blockquote.removeChild(line);
      } else if (trailingBr && lastChild) {
        blockquote.removeChild(lastChild);
      }

      const newLine = document.createElement('div');
      newLine.appendChild(document.createElement('br'));
      blockquote.parentNode?.insertBefore(newLine, blockquote.nextSibling);

      if (textIsEmpty(blockquote) && !blockquote.children.length) {
        blockquote.remove();
      }

      const newRange = document.createRange();
      newRange.setStart(newLine, 0);
      newRange.collapse(true);
      sel.removeAllRanges();
      sel.addRange(newRange);
    }
  };

  const getEditorContent = () => {
    return editorRef.current?.innerText.trim() || '';
  };

  /**
   * The editor's markup, for a field stored as HTML.
   *
   * `getEditorContent()` returns `innerText`, which suits plain-text consumers,
   * but the study-group announcement is stored as HTML and its toolbar applies
   * bold, italic, underline and blockquote, so `innerText` would drop every
   * format on save.
   */
  const getEditorHtml = () => editorRef.current?.innerHTML ?? '';

  /**
   * Load stored markup into the editor. Call it once per loaded record (a ref
   * latch), or a refetch overwrites what the author is typing.
   */
  const setEditorHtml = (html: string) => {
    if (editorRef.current) {
      editorRef.current.innerHTML = html;
    }
  };

  const clearEditor = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
  };

  return {
    editorRef,
    applyFormat,
    insertList,
    insertBlockquote,
    saveSelection,
    applyLink,
    handleKeyDown,
    getEditorContent,
    getEditorHtml,
    setEditorHtml,
    clearEditor,
  };
}
